import type {
  AgentMembers,
  AgentRole,
  AgentShareImpactResponse,
  AgentTeamMember,
  AgentUserMember,
  DataResponse,
  EntityTeamSharesResponse,
  SharePermission,
  TeamShareResponse,
} from "@/types";
import { api } from "../api";

export const sharingService = {
  getTeamShares: (entityType: string, entityUuid: string) =>
    api.get<DataResponse<EntityTeamSharesResponse>>(
      `/sharing/teams?entity_type=${entityType}&entity_uuid=${entityUuid}`,
    ),
  shareWithTeam: (data: { entityType: string; entityUuid: string; teamUuid: string; permission: SharePermission }) =>
    api.post<DataResponse<TeamShareResponse>>("/sharing/teams", data),
  removeTeamShare: (shareUuid: string) => api.delete(`/sharing/teams/${shareUuid}`),
};

/**
 * Agent membership hits dedicated, admin-gated endpoints
 * (`/agents/{uuid}/members/*`). Managing membership requires the agent `admin`
 * role (or platform admin).
 */
export const agentMembershipService = {
  getMembers: (agentUuid: string) => api.get<DataResponse<AgentMembers>>(`/agents/${agentUuid}/members`),

  addUserMember: (agentUuid: string, userUuid: string, role: AgentRole) =>
    api.post<DataResponse<AgentUserMember>>(`/agents/${agentUuid}/members/users`, { userUuid, role }),
  updateUserMemberRole: (agentUuid: string, userUuid: string, role: AgentRole) =>
    api.patch<DataResponse<AgentUserMember>>(`/agents/${agentUuid}/members/users/${userUuid}`, { role }),
  removeUserMember: (agentUuid: string, userUuid: string) =>
    api.delete(`/agents/${agentUuid}/members/users/${userUuid}`),

  addTeamMember: (agentUuid: string, teamUuid: string, role: AgentRole) =>
    api.post<DataResponse<AgentTeamMember>>(`/agents/${agentUuid}/members/teams`, { teamUuid, role }),
  updateTeamMemberRole: (agentUuid: string, teamUuid: string, role: AgentRole) =>
    api.patch<DataResponse<AgentTeamMember>>(`/agents/${agentUuid}/members/teams/${teamUuid}`, { role }),
  removeTeamMember: (agentUuid: string, teamUuid: string) =>
    api.delete(`/agents/${agentUuid}/members/teams/${teamUuid}`),

  /**
   * Advisory check: which of the agent's extensions the target can't use.
   * Pass exactly one of teamUuid / userUuid. Never blocks membership changes.
   */
  getMemberImpact: (agentUuid: string, target: { teamUuid?: string; userUuid?: string }) => {
    const params = new URLSearchParams();
    if (target.teamUuid) params.set("team_uuid", target.teamUuid);
    if (target.userUuid) params.set("user_uuid", target.userUuid);
    return api.get<DataResponse<AgentShareImpactResponse>>(`/agents/${agentUuid}/members/impact?${params}`);
  },
};
